import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { TicketingForm, TicketingTool } from 'shared/types';
import { TICKETING } from '@elementary/common-ui/constants/queryKeys';
import useSnackbar from '../useSnackbar';
import * as api from '../../services/settingsApi';
import useSendTracking from './useSendTracking';

type Props = {
  envId?: string;
  onUpdateSuccess?: () => void;
};

const useTicketingIntegration = ({ envId, onUpdateSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const ticketingQuery = useQuery(
    [TICKETING, envId],
    () => api.fetchTicketing(envId!),
    {
      enabled: !!envId,
    },
  );

  const ticketingProjectsMutation = useMutation(
    (ticketingType: TicketingTool) =>
      api.fetchTicketingProjects(envId!, ticketingType),
    {
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.FETCH_TICKETING_PROJECTS_FAILURE,
        }),
    },
  );

  const ticketingMutation = useMutation(
    (data: TicketingForm) => {
      const reqData = {
        enabled: true,
        project_id: data.projectId,
        type: data.type,
        name: data.name,
      };

      return data.id
        ? api.updateTicketing(data.id, reqData, envId!)
        : api.createTicketing(reqData, envId!);
    },
    {
      onSuccess: (data: TicketingForm, variables: TicketingForm) => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([TICKETING, envId!]);
        openSnackbar({
          message: `Successfully ${
            variables.id ? 'updated' : 'connected to'
          } your ticketing system`,
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName: trackingEvents?.CONNECT_TICKETING_INTEGRATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.CONNECT_TICKETING_INTEGRATION_FAILURE,
        }),
    },
  );

  const deleteTicketingMutation = useMutation(
    (id: string) => api.deleteTicketing({ id, envId: envId! }),
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([TICKETING, envId]);
        openSnackbar({
          message: 'Successfully disconnected from your ticketing system',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName:
            trackingEvents?.DISCONNECT_TICKETING_INTEGRATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) => {
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.DISCONNECT_TICKETING_INTEGRATION_FAILURE,
        });
        openSnackbar({
          message: 'Failed to disconnect from your ticketing system',
          variant: 'error',
        });
      },
    },
  );

  return {
    ticketingQuery,
    ticketingProjectsMutation,
    ticketingMutation,
    deleteTicketingMutation,
    isTicketingConnected:
      !ticketingQuery.isLoading && !!ticketingQuery.data?.integrations?.length,
  };
};

export default useTicketingIntegration;
